import type { CSSProperties } from 'react';

import { DummyAvatar } from '@/ui/icons/DummyAvatar';
import { hashToNumber } from '@/utils';

import type { ProductReview } from '../../types';
import { StarRating } from './StarRating';

interface ReviewCardProps {
  review: ProductReview;
  showDate?: boolean;
  className?: string;
  style?: CSSProperties;
}

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

export function ReviewCard({ review, showDate = false, className, style }: ReviewCardProps) {
  const avatarIndex = hashToNumber(review.id);
  const formattedDate = showDate && review.created_at ? formatDate(review.created_at) : null;

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        width: '100%',
        ...style,
      }}
    >
      <div
        style={{
          width: '32px',
          height: '32px',
          borderRadius: '100px',
          border: '1px solid var(--border-highlight)',
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <DummyAvatar index={avatarIndex} className="w-full h-full rounded-full" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontWeight: 'var(--product-reviews-feed-author-font-weight, 510)',
              fontSize: 'var(--product-reviews-feed-author-font-size, 14px)',
              fontFamily: 'var(--product-reviews-feed-author-font-family, inherit)',
              lineHeight: '24px',
              color: 'var(--product-reviews-feed-author-color, var(--text))',
            }}
          >
            {review.name}
          </span>
          <StarRating score={review.score} size={16} />
          {formattedDate && (
            <span
              style={{
                fontSize: 'var(--product-reviews-feed-date-font-size, 12px)',
                fontFamily: 'var(--product-reviews-feed-date-font-family, inherit)',
                lineHeight: '24px',
                color: 'var(--product-reviews-feed-date-color, var(--text-light))',
              }}
            >
              {formattedDate}
            </span>
          )}
        </div>

        <p
          style={{
            margin: 0,
            textAlign: 'left',
            fontSize: 'var(--product-reviews-feed-text-font-size, 14px)',
            fontFamily: 'var(--product-reviews-feed-text-font-family, inherit)',
            lineHeight: '24px',
            color: 'var(--product-reviews-feed-text-color, var(--text-light))',
          }}
        >
          {review.review}
        </p>
      </div>
    </div>
  );
}
